/**
 * Per-shot geo binding — the X-SV-Geo header string (`{lat,lng,accuracyM}`) bound into every chunk
 * of a shot. Distinct from the visit-level POST /geo row (media/useGeoEvidence.ts): the row is the
 * G1 leg; the header is what ties each chunk to where the device said it was when the shot began.
 *
 * `latestGeoHeader()` is kept fresh by the capture's ONE shared `watchPosition` (capture/useGeoWatch.ts — US-03482 G2:
 * no per-shot request, each was a new acquisition and a new prompt on Chrome for iOS), which calls `rememberFix` on
 * every callback. No fix at all → null → the header is omitted and the chain binds the empty string — the server sees
 * an honest absence, never a fabricated coordinate.
 *
 * The SAME `latest` is what the viewfinder's LocationCard reads (flow/components/LocationCard.tsx):
 * one source of truth for the card and the header, so the card can never show a value the chunk
 * will not bind. `subscribeFix` lets the card re-render when the fix changes, and the shot handle exposes the frozen
 * bound header for the card's `bound` state (M3, SPEC_STAMP_ROUND §C.4).
 */

export interface GeoFix {
  readonly lat: number;
  readonly lng: number;
  readonly accuracyM: number;
  readonly collectedAt: string;
  readonly mockFlag: true | false | null;
  readonly altitudeM?: number;
  readonly speedMps?: number;
}

let latest: GeoFix | null = null;
/** The visit the held fix must belong to (US-03482 P1-3; `beginVisitGeo` below). */
let visitGeo: { visitId: string; startedAt: number } | null = null;
const listeners = new Set<() => void>();

function notify(): void {
  for (const l of listeners) l();
}

export function rememberFix(fix: GeoFix): void {
  latest = fix;
  notify();
}

export function latestFix(): GeoFix | null {
  return latest;
}

/** Re-render hook for the viewfinder card; returns the unsubscribe. */
export function subscribeFix(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function resetGeoForTests(): void {
  latest = null;
  visitGeo = null;
  heldAccepted = false;
  gateOpened = false;
  notify();
}

/**
 * US-03482 P1-3 — the location gate asks for a fix OF THIS VISIT. A fix held in this module from an earlier visit in the
 * same tab is dropped when a different visit begins, and a fix is current only when it was collected after the visit
 * began, less the 30 s `maximumAge` every request here accepts (a browser may hand back a fix that old, legitimately).
 */
const ACCEPTED_FIX_AGE_MS = 30_000;

export function beginVisitGeo(visitId: string, now: number = Date.now()): void {
  if (visitGeo?.visitId === visitId) return;
  visitGeo = { visitId, startedAt: now };
  heldAccepted = false;
  gateOpened = false;
  if (latest !== null) {
    latest = null;
    // Called from a render (CaptureFlow): tell the other subscribers after it, never during it.
    queueMicrotask(notify);
  }
}

/** The fix belongs to this visit (collected after it began, less the accepted `maximumAge`). Accuracy aside. */
export function isVisitFix(fix: GeoFix | null): fix is GeoFix {
  if (!fix) return false;
  if (!visitGeo) return true;
  const at = Date.parse(fix.collectedAt);
  return Number.isNaN(at) || at >= visitGeo.startedAt - ACCEPTED_FIX_AGE_MS;
}

/**
 * US-03482 E4 (decided 2026-11-03): a rough position never blocks capture. A fix worse than ROUGH_ACCURACY_M is held
 * back only while the gate tries for a better one (up to ~15 s); then the gate accepts it for the rest of the visit
 * (`acceptHeldFix`). The accuracy travels in the chunk header as always, so the report can say "approximate (±N m)".
 *
 * The same wait covers a fix whose OWN timestamp says it is older than the visit although it answered a request of
 * this visit (a held fix was dropped when the visit began): the device clock and the position's clock disagree (a
 * hand-set phone clock against GNSS time; WebKit bug 186689 shipped an epoch-wrong timestamp). The capture's one watch
 * asks with `maximumAge: 5 s` (capture/useGeoWatch.ts), so a fix that still reads "old" after the wait is fresh by the
 * browser's contract — refusing it for ever would strand a phone that IS giving positions. Its `collectedAt` travels on
 * the G1 row as reported.
 */
export const ROUGH_ACCURACY_M = 500;
let heldAccepted = false;

export function isRoughFix(fix: GeoFix): boolean {
  return !(fix.accuracyM <= ROUGH_ACCURACY_M);
}

export function acceptHeldFix(): void {
  if (heldAccepted) return;
  heldAccepted = true;
  // A NEW snapshot for useLatestFix (useSyncExternalStore re-renders only when the snapshot changes): with one shared
  // watch a stationary phone may never call back again, and the screens must re-read isCurrentFix now. Same values.
  if (latest !== null) latest = { ...latest };
  notify();
}

/**
 * The fix the location gate opens on: of this visit, and precise enough — or, once the gate has waited
 * (`acceptHeldFix`), whatever this phone holds: rough (E4), or old by its own clock.
 */
export function isCurrentFix(fix: GeoFix | null): boolean {
  if (!fix) return false;
  if (heldAccepted) return true;
  return isVisitFix(fix) && !isRoughFix(fix);
}

/**
 * The gate stands in front of the FIRST shot of a visit (§16i "no shot starts until one position fix of this visit is
 * held"), never in the middle of one: once this visit has held a current fix it stays open. A later reading — a rough
 * one indoors from the viewfinder's watch or a shot-start snapshot — would otherwise unmount the camera screen mid-clip;
 * that reading's accuracy still travels with every chunk it binds. A new visit (or a reload) closes it again.
 */
let gateOpened = false;

export function visitGateOpen(fix: GeoFix | null): boolean {
  if (isCurrentFix(fix)) gateOpened = true;
  return gateOpened;
}

/** The exact X-SV-Geo header string for a fix (key order fixed; raw numbers as reported). */
export function geoHeader(fix: GeoFix): string {
  return `{"lat":${fix.lat},"lng":${fix.lng},"accuracyM":${fix.accuracyM}}`;
}

export function latestGeoHeader(): string | null {
  return latest ? geoHeader(latest) : null;
}

export function fixFromPosition(pos: GeolocationPosition): GeoFix {
  const c = pos.coords as GeolocationCoordinates & { mocked?: unknown };
  const out: {
    lat: number; lng: number; accuracyM: number; collectedAt: string; mockFlag: true | false | null;
    altitudeM?: number; speedMps?: number;
  } = {
    lat: c.latitude,
    lng: c.longitude,
    accuracyM: c.accuracy,
    collectedAt: new Date(pos.timestamp).toISOString(),
    // Non-standard Android-WebView hint; browsers cannot detect OS mock location — null is the
    // honest "not detectable" default (never coerced to false).
    mockFlag: typeof c.mocked === 'boolean' ? c.mocked : null,
  };
  if (typeof c.altitude === 'number') out.altitudeM = c.altitude;
  if (typeof c.speed === 'number') out.speedMps = c.speed;
  return out;
}
